import React, { useEffect, useState } from "react";
import "./LeaveSummary.css";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { format, addDays, subDays } from "date-fns";
import axios from "axios";
import { base_url as base_url_from_axios_config } from "../../utils/AxiosConfig";

ChartJS.register(ArcElement, Tooltip, Legend);

// ── Design-system palette (matches AttendanceBubbleChart / global) ──────────
const CHART_COLORS = [
  "#3B82F6", // blue-500
  "#A855F7", // purple-500
  "#EAB308", // yellow-500
  "#EF4444", // red-500
  "#22C55E", // green-500
];

const LeaveSummary = () => {
  const storedUserDataArray =
    JSON.parse(localStorage.getItem("BondTimeuserDataArray")) || [];
  let companyCode = null;
  if (storedUserDataArray) {
    companyCode = storedUserDataArray[0].cmpcode;
  }

  const [currentDate, setCurrentDate] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState("");
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePrevDate = () => setCurrentDate((prev) => subDays(prev, 1));
  const handleNextDate = () => setCurrentDate((prev) => addDays(prev, 1));

  const [base_url, setBaseUrl] = useState(`${base_url_from_axios_config}/`);

  const options = {
    cutout: "78%",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.parsed}`,
        },
      },
    },
  };

  const fetchLeaveData = async () => {
    setLoading(true);
    const apiDate = format(currentDate, "dd-MMM-yyyy").toLowerCase();
    const apiUrl = `${base_url}api/HRDashboard/${companyCode}/-/LEAVECOUNT/${apiDate}/${apiDate}/-/-/-/-/-`;
    console.log("apiUrl-AttendanceAnalysis", apiUrl);

    try {
      const response = await axios.get(apiUrl);
      if (response.status === 200) {
        const apiData = response.data;
        const labels = apiData.map((item) => item.leave_key);
        const dataValues = apiData.map((item) => item.count);

        setChartData({
          labels,
          datasets: [
            {
              data: dataValues,
              backgroundColor: CHART_COLORS,
              hoverBackgroundColor: CHART_COLORS,
              borderWidth: 2,
              borderColor: "#ffffff",
              hoverBorderWidth: 0,
            },
          ],
        });
        setAttendanceData(response.data);
      }
    } catch (error) {
      console.error("Error fetchLeaveData:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (base_url) fetchLeaveData();
  }, [currentDate, base_url]);

  const totalCount = chartData
    ? chartData.datasets[0].data.reduce((a, b) => a + b, 0)
    : 0;

  const isToday =
    format(currentDate, "dd MMM yyyy") === format(new Date(), "dd MMM yyyy");

  return (
    <div className="w-full bg-white rounded-2xl overflow-hidden">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="flex items-center justify-between  border-b border-slate-100 px-5 pt-5 pb-3">
        <div className="">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
            Summary
          </p>
          <h2 className="text-base font-bold text-slate-900 leading-tight mt-0.5">
            Attendance Analysis
          </h2>
        </div>

        {/* Date navigator */}
        <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1.5">
          <button
            onClick={handlePrevDate}
            className="flex h-6 w-6 items-center justify-center rounded-lg text-slate-400 transition hover:bg-white hover:text-slate-700 hover:shadow-sm"
          >
            <MdOutlineKeyboardArrowLeft size={18} />
          </button>
          <span
            className={`min-w-[90px] text-center text-xs font-semibold ${
              isToday ? "text-blue-600" : "text-slate-600"
            }`}
          >
            {isToday ? "Today" : format(currentDate, "dd MMM yyyy")}
          </span>
          <button
            onClick={handleNextDate}
            className="flex h-6 w-6 items-center justify-center rounded-lg text-slate-400 transition hover:bg-white hover:text-slate-700 hover:shadow-sm"
          >
            <MdOutlineKeyboardArrowRight size={18} />
          </button>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="px-5 py-4">
        {/* Loading skeleton */}
        {loading && (
          <div className="flex items-center justify-center gap-6 py-4">
            <div className="h-12 w-12 animate-pulse rounded-full bg-slate-100" />
            <div className="flex flex-col gap-2.5">
              {[80, 64, 72, 56, 68].map((w, i) => (
                <div
                  key={i}
                  className="h-3 animate-pulse rounded-full bg-slate-100"
                  style={{ width: `${w}px` }}
                />
              ))}
            </div>
          </div>
        )}

        {/* No data */}
        {!loading && !chartData && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
              <span className="text-2xl">📊</span>
            </div>
            <p className="text-sm font-medium text-slate-500">No data available</p>
            <p className="mt-0.5 text-xs text-slate-400">
              {format(currentDate, "dd MMM yyyy")}
            </p>
          </div>
        )}

        {/* Chart + Legend */}
        {!loading && chartData && (
          <div className="flex flex-col items-center gap-5">
            {/* Doughnut with centre label */}
            <div className="relative flex-shrink-0 h-[220px] w-[220px] ">
              <Doughnut data={chartData} options={options} />
              {/* Centre total */}
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-bold text-slate-800 leading-none">
                  {totalCount}
                </span>
                <span className="mt-0.5 text-[10px] font-medium text-slate-400 uppercase tracking-wide">
                  Total
                </span>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-1 flex-col gap-2 border-l border-slate-100 pl-5">
              {chartData.labels.map((label, index) => {
                const count = chartData.datasets[0].data[index];
                const color = CHART_COLORS[index % CHART_COLORS.length];
                const pct = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;

                return (
                  <div key={index} className="flex items-center gap-2">
                    {/* Color dot */}
                    <span
                      className="h-2 w-2 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    {/* Label */}
                    <span className="flex-1 truncate text-xs font-medium text-slate-600">
                      {label}
                    </span>
                    {/* Count badge */}
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                      style={{
                        backgroundColor: `${color}18`,
                        color,
                      }}
                    >
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveSummary;